/* eslint-disable max-lines */
import { FeatureEntitlementName } from '@jupiterone/entitlement-types';
import { AbacRuleName } from '@jupiterone/jupiter-types';
import {
  Award as AwardIcon,
  Bell as BellIcon,
  CheckCircle as CheckCircleIcon,
  Layers as LayersIcon,
  Sun as SunIcon,
} from 'react-feather';
import ClipboardIcon from './icons/Clipboard';
import MeshIcon from './icons/Mesh';
import PlanetIcon from './icons/Planet';
import { HeadBarConfig } from './types';
import { IS_DEPLOYED_ENV } from './urls';
import { getHostEnv } from './util/url';

const hostEnv = getHostEnv();

export const pendoApiKey = 'fb1db7b9-6ba3-49d3-5f1c-0faaa4e71349';
export const SENTRY_DSN =
  'https://925970d7aada463e9562edaf6e67893c@o426749.ingest.sentry.io/5369921';
export const audienceUri =
  IS_DEPLOYED_ENV && hostEnv !== 'dev'
    ? 'urn:amazon:cognito:sp:us-east-2_9fnMVHuxD'
    : 'urn:amazon:cognito:sp:us-east-1_KzFbFevHz';
export const IP_LOOKUP_BASE_URL = 'https://tools.keycdn.com/geo?host=';

export const MANAGE_JUPITERONE_PRICING_PLAN = '/products';

export const LANDING_PATH = '/landing';
export const MY_SECURITY_PATH = '/my-security';
export const ASSETS_PATH = '/assets';
export const INVENTORY_PATH = '/inventory';
export const INVENTORY_ALL_PATH = `${INVENTORY_PATH}/all`;
export const INVENTORY_CRITICAL_PATH = `${INVENTORY_PATH}/critical`;
export const POLICIES_PATH = '/policies';
export const ALERTS_PATH = '/alerts';
export const COMPLIANCE_PATH = '/compliance';
export const COMPLIANCE_V2_PATH = '/v2compliance';
export const GRAPH_VIEWER_PATH = '/galaxy';
export const INSIGHTS_PATH = '/insights';
export const INTEGRATIONS_PATH = '/integrations';
export const ONBOARDING_PATH = '/onboarding';
export const ONBOARDING_V2_PATH = '/welcome';
export const UPGRADE_PATH = '/upgrade';
export const BILLING_PATH = '/products';
export const POWERUPS_PATH = '/powerups';
export const AUDIT_EVENTS_PATH = '/audit-events';

export const J1_LOGO_BLACK_TEXT =
  'https://raw.githubusercontent.com/JupiterOne/docs/main/assets/brand/logo-black-text.png';

export const TIME_PROPERTY_REGEX = /^((.+(On|At|_on|_at|Date|Time(stamp)?|Changed|Updated|Created|LastUsed))|lastLogin|lastUsed|activated|created)$/;
export const IPV4_REGEX = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}(\/[0-9]{1,2})?$/;
export const URI_REGEX = /^https?:\/\/.*/i;
export const MARKDOWN_REGEX = /^#.*$/gm;
export const MONEY_REGEX = /cost|dollar/i;
export const URI_INVALID_CHARACTERS = ';/?:@&=+$,{}()|\\^[]`.~'.split(''); // Character Array - this is used for tests
export const SESSION_STORAGE_KEY = 'jupiterone-auth';
export const J1_ACCOUNT_HEADER = 'lifeomic-account';
export const NAVBAR_HEIGHT = 60;
export const ALPHANUMERIC_REGEX = /[\W_]+/g;
export const ISO_DATE_REGEX = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}.+/;

function exists(obj: any): Boolean {
  return Boolean(obj);
}

export const APPS = (options: {
  withInsights: boolean;
  withMySecurity: boolean;
  withLanding: boolean;
  withPolicies: boolean;
  withCompliance: boolean;
  withGalaxy: boolean;
  withAssets: boolean;
  withRules: boolean;
}): HeadBarConfig[] =>
  [
    options.withLanding && {
      name: 'Landing',
      icon: PlanetIcon,
      url: LANDING_PATH,
      featureEntitlementName: FeatureEntitlementName.LANDING,
    },
    options.withMySecurity && {
      name: 'My Security',
      icon: AwardIcon,
      url: MY_SECURITY_PATH,
      beta: true,
      featureEntitlementName: FeatureEntitlementName.MY_SECURITY,
    },
    options.withAssets && {
      name: 'Assets',
      icon: ClipboardIcon,
      url: ASSETS_PATH,
      featureEntitlementName: FeatureEntitlementName.INVENTORY,
    },
    options.withPolicies && {
      name: 'Policies',
      icon: LayersIcon,
      url: POLICIES_PATH,
      featureEntitlementName: FeatureEntitlementName.POLICIES,
    },
    options.withRules && {
      name: 'Alerts',
      icon: BellIcon,
      url: ALERTS_PATH,
      featureEntitlementName: FeatureEntitlementName.ALERTS,
    },
    options.withCompliance && {
      name: 'Compliance',
      icon: CheckCircleIcon,
      url: COMPLIANCE_PATH,
      featureEntitlementName: FeatureEntitlementName.COMPLIANCE,
    },
    options.withGalaxy && {
      name: 'Graph Viewer',
      icon: MeshIcon,
      url: GRAPH_VIEWER_PATH,
      featureEntitlementName: FeatureEntitlementName.GRAPH_VIEWER,
    },
    options.withInsights && {
      name: 'Insights',
      icon: SunIcon,
      url: INSIGHTS_PATH,
      featureEntitlementName: FeatureEntitlementName.INSIGHTS,
    },
  ].filter(exists) as HeadBarConfig[];

export enum JupiterOneApps {
  Assets = 'Assets',
  AuditEvents = 'Audit Events',
  Compliance = 'Compliance',
  EndpointCompliance = 'Endpoint Compliance',
  Galaxy = 'Galaxy',
  Insights = 'Insights',
  Integrations = 'Integrations',
  Landing = 'Landing',
  MySecurity = 'My Security',
  Policies = 'Policies',
  Rules = 'Rules',
}

const GLOBAL_READ_PERMISSIONS: AbacRuleName[] = [
  'fullReadAccess',
  'accessAdmin',
];
export const APP_ACCESS_PERMISSIONS: Record<JupiterOneApps, AbacRuleName[]> = {
  [JupiterOneApps.Assets]: [
    'accessAssets',
    'adminAssets',
    ...GLOBAL_READ_PERMISSIONS,
  ],
  [JupiterOneApps.AuditEvents]: ['accessAdmin'],
  [JupiterOneApps.Compliance]: [
    'accessCompliance',
    'adminCompliance',
    ...GLOBAL_READ_PERMISSIONS,
  ],
  [JupiterOneApps.EndpointCompliance]: [
    'accessEndpointCompliance',
    'adminEndpointCompliance',
    ...GLOBAL_READ_PERMISSIONS,
  ],
  [JupiterOneApps.Galaxy]: [
    'accessGalaxy',
    'adminGalaxy',
    ...GLOBAL_READ_PERMISSIONS,
  ],
  [JupiterOneApps.Insights]: [
    'accessInsights',
    'adminInsights',
    ...GLOBAL_READ_PERMISSIONS,
  ],
  [JupiterOneApps.Integrations]: [
    'accessIntegrations',
    'adminIntegrations',
    ...GLOBAL_READ_PERMISSIONS,
  ],
  [JupiterOneApps.Landing]: [
    'accessLanding',
    'adminLanding',
    ...GLOBAL_READ_PERMISSIONS,
  ],
  [JupiterOneApps.MySecurity]: [
    'accessMySecurity',
    'adminMySecurity',
    ...GLOBAL_READ_PERMISSIONS,
  ],
  [JupiterOneApps.Policies]: [
    'accessPolicies',
    'adminPolicies',
    ...GLOBAL_READ_PERMISSIONS,
  ],
  [JupiterOneApps.Rules]: [
    'accessRules',
    'adminRules',
    ...GLOBAL_READ_PERMISSIONS,
  ],
};

export const SEC_POLICY = {
  _type: 'security_policy',
  _class: ['Document', 'Policy'],
};

export const SEC_PROCEDURE = {
  _type: 'security_procedure',
  _class: ['Document', 'Procedure'],
};

export const SEC_DOCUMENT = {
  _type: 'security_document',
  _class: ['Document'],
};

export const POLICY_PROCEDURE_RELATION = {
  _type: 'procedure|implements|policy',
  _class: 'IMPLEMENTS',
};

// J1QL

export const ACCOUNT_ENTITY_J1QL = `
  FIND Account
  WITH _type="jupiterone_account"
  AS account
  RETURN account
`;

export const ACCOUNT_POLICY_ENTITIES_J1QL = `
  FIND (security_policy|security_procedure|security_document)
`;

export const ACCOUNT_POLICY_TREE_J1QL = `
  FIND Root
  THAT HAS security_policy
  THAT relates to (security_procedure|security_document)
  RETURN tree
`;

export const POLICIES_SCOPE = 'policies-procedures';
